import { Link, useLocation } from 'react-router-dom';
import { Home, MessageSquare, Menu, X, Settings, LogOut, Users, Mail, Cpu, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { getAdminAudience, getAdminInquiriesByArchive, getChatNotificationSummary, getFriendRequests, getMyContacts } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { getMutedTextColor, getPanelStyle, getSoftSurface, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

export const HeaderWithMailbox = ({ user, theme, isMobileMenuOpen, setIsMobileMenuOpen, handleLogout }: any) => {
    const location = useLocation();
    const { lang, setLang, t } = useI18n();
    const [unreadCount, setUnreadCount] = useState(0);
    const [audienceUnreadCount, setAudienceUnreadCount] = useState(0);
    const [neighborUnreadCount, setNeighborUnreadCount] = useState(0);
    const [salonUnreadCount, setSalonUnreadCount] = useState(0);

    const navItems = [
        { path: '/', label: t('ホーム'), icon: <Home size={20} /> },
        { path: '/neighbors', label: t('隣人'), icon: <Users size={20} />, badge: neighborUnreadCount > 0 ? neighborUnreadCount : null },
        { path: '/salon', label: t('サロン'), icon: <MessageSquare size={20} />, requiresSalon: true, badge: salonUnreadCount > 0 ? salonUnreadCount : null },
        { path: '/toku', label: 'Toku', icon: <Trophy size={20} /> },
        { path: '/u-store', label: 'U-Store', icon: <Trophy size={20} />, requiresUnito: true },
        { path: '/admin', label: t('管理'), icon: <Settings size={20} />, role: ['Admin'] },
        { path: '/ai-admin', label: t('AI管理'), icon: <Cpu size={20} />, role: ['Admin', 'Manager'] },
        {
            path: '/mailbox',
            label: t('メールボックス'),
            icon: <Mail size={20} />,
            role: ['Admin', 'Manager'],
            badge: unreadCount > 0 ? unreadCount : null,
        },
        {
            path: '/audience',
            label: t('謁見'),
            icon: <MessageSquare size={20} />,
            isAudience: true,
            badge: audienceUnreadCount > 0 ? audienceUnreadCount : null,
        },
        {
            path: '/contact',
            label: t('管理人へ連絡'),
            icon: <MessageSquare size={20} />,
            isContact: true,
        },
    ];

    const canSee = (item: any) => {
        if (!user) return false;
        if (item.requiresSalon && !user.salon_enabled) return false;
        if (item.requiresUnito && !(user.unito_enabled || user.ustore_enabled)) return false;
        if (item.role && !item.role.includes(user.role_type)) return false;

        if (item.isAudience) {
            if (['Admin', 'Maestro'].includes(user.role_type)) return true;
            if (user.salon_enabled) return true;
            if (user.unito_enabled || user.ustore_enabled) return true;
            if (user.floor_level === 10) return true;
            return false;
        }

        if (item.isContact) {
            if (['Admin', 'Manager', 'Maestro'].includes(user.role_type)) return false;
            return true;
        }

        return true;
    };

    useEffect(() => {
        if (user && ['Admin', 'Manager'].includes(user.role_type)) {
            const fetchUnread = async () => {
                try {
                    const res = await getAdminInquiriesByArchive(false);
                    setUnreadCount(res.data.filter((i: any) => !i.is_read).length);
                } catch (e) {
                }
            };
            fetchUnread();
            const interval = setInterval(fetchUnread, 30000);
            return () => clearInterval(interval);
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        const fetchNeighborUnread = async () => {
            try {
                const res = await getFriendRequests();
                setNeighborUnreadCount(res.data.length || 0);
            } catch (e) {
            }
        };
        fetchNeighborUnread();
        const interval = setInterval(fetchNeighborUnread, 30000);
        return () => clearInterval(interval);
    }, [user]);

    useEffect(() => {
        if (!user || !user.salon_enabled) {
            setSalonUnreadCount(0);
            return;
        }
        const fetchSalonUnread = async () => {
            try {
                const res = await getChatNotificationSummary();
                const total = (res.data?.total_mentions || 0) + (res.data?.total_replies || 0);
                setSalonUnreadCount(total);
            } catch (e) {
            }
        };
        fetchSalonUnread();
        const interval = setInterval(fetchSalonUnread, 30000);
        return () => clearInterval(interval);
    }, [user]);

    useEffect(() => {
        if (!user) return;

        const canViewAudience = () => {
            if (['Admin', 'Maestro'].includes(user.role_type)) return true;
            if (user.salon_enabled) return true;
            if (user.unito_enabled) return true;
            if (user.floor_level === 10) return true;
            return false;
        };

        if (!canViewAudience()) {
            setAudienceUnreadCount(0);
            return;
        }

        const fetchAudienceUnread = async () => {
            try {
                if (['Admin', 'Maestro'].includes(user.role_type)) {
                    const res = await getAdminAudience();
                    const unread = res.data.filter(
                        (i: any) => !i.is_read && i.user_id !== user.user_id
                    ).length;
                    setAudienceUnreadCount(unread);
                } else {
                    const res = await getMyContacts('audience');
                    const unread = res.data.filter(
                        (i: any) => !i.is_read && i.recipient_id === user.user_id
                    ).length;
                    setAudienceUnreadCount(unread);
                }
            } catch (e) {
            }
        };

        fetchAudienceUnread();
        const interval = setInterval(fetchAudienceUnread, 30000);
        return () => clearInterval(interval);
    }, [user]);

    const darkTheme = isDarkTheme(theme);
    const headerStyle = getPanelStyle(theme, 0.97, 0.9);
    const mutedTextColor = getMutedTextColor(theme);
    const primaryTextColor = pickTextColor(theme.colors.primary);
    const activeChipBackground = withAlpha(theme.colors.primary, darkTheme ? 0.2 : 0.14);

    return (
        <header
            className={`sticky top-4 z-50 border backdrop-blur-xl transition-all duration-500 mx-4 mt-2 ${theme.radius} ${theme.shadow}`}
            style={headerStyle}
        >
            <div className="container mx-auto px-4 py-3">
                <div className="flex items-start justify-between gap-3 md:items-center">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xl font-serif font-bold tracking-[0.14em]" style={{ color: theme.colors.primary }}>
                                Maison Portal
                            </span>
                            <span
                                className="max-w-full rounded-full border px-2.5 py-1 text-[11px] font-medium"
                                style={{
                                    borderColor: theme.colors.border,
                                    color: theme.colors.accent,
                                    backgroundColor: getSoftSurface(theme, 0.78, 0.45),
                                }}
                            >
                                {user && !['Admin', 'Maestro', 'Manager'].includes(user.role_type) ? `Floor ${user.floor_level} ` : ''}
                                {theme.name}
                            </span>
                        </div>
                        <div className="mt-1 text-xs" style={{ color: mutedTextColor }}>
                            {theme.description}
                        </div>
                    </div>

                    <button
                        className="md:hidden rounded-full border p-2 transition-colors"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        style={{
                            color: theme.colors.text,
                            borderColor: theme.colors.border,
                            backgroundColor: getSoftSurface(theme, 0.86, 0.52),
                        }}
                        aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                    >
                        {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>

                <div className="mt-3 hidden md:flex flex-wrap items-center justify-between gap-3 border-t pt-3" style={{ borderColor: withAlpha(theme.colors.border, 0.6) }}>
                    <nav className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
                        {navItems.filter(canSee).map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className="relative inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all hover:opacity-90"
                                    style={{
                                        color: isActive ? theme.colors.primary : theme.colors.text,
                                        borderColor: isActive ? theme.colors.primary : withAlpha(theme.colors.border, 0.7),
                                        backgroundColor: isActive ? activeChipBackground : 'transparent',
                                    }}
                                >
                                    {item.icon}
                                    {item.label}
                                    {item.badge ? (
                                        <span
                                            className="inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                                            style={{
                                                backgroundColor: theme.colors.primary,
                                                color: primaryTextColor,
                                            }}
                                        >
                                            {item.badge}
                                        </span>
                                    ) : null}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <Link to="/settings">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="gap-1.5 px-3 text-xs"
                                style={{
                                    color: theme.colors.text,
                                    borderColor: theme.colors.border,
                                    backgroundColor: getSoftSurface(theme, 0.86, 0.5),
                                }}
                            >
                                <Settings size={16} />
                                <span className="font-medium">{t('制御室')}</span>
                            </Button>
                        </Link>
                        <div
                            className="flex items-center gap-1 rounded-full border px-2 py-1 text-[10px]"
                            style={{
                                borderColor: theme.colors.border,
                                backgroundColor: getSoftSurface(theme, 0.84, 0.48),
                            }}
                        >
                            <button
                                className="rounded-full px-2 py-0.5"
                                onClick={() => setLang('en')}
                                type="button"
                                style={{
                                    backgroundColor: lang === 'en' ? theme.colors.primary : 'transparent',
                                    color: lang === 'en' ? primaryTextColor : mutedTextColor,
                                }}
                            >
                                E
                            </button>
                            <button
                                className="rounded-full px-2 py-0.5"
                                onClick={() => setLang('ja')}
                                type="button"
                                style={{
                                    backgroundColor: lang === 'ja' ? theme.colors.primary : 'transparent',
                                    color: lang === 'ja' ? primaryTextColor : mutedTextColor,
                                }}
                            >
                                J
                            </button>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleLogout}
                            className="gap-1.5 px-3 text-xs"
                            style={{
                                color: '#ef4444',
                                borderColor: withAlpha('#ef4444', 0.4),
                                backgroundColor: withAlpha('#ef4444', darkTheme ? 0.12 : 0.08),
                            }}
                        >
                            <LogOut size={16} />
                            <span>{t('ログアウト')}</span>
                        </Button>
                    </div>
                </div>
            </div>
        </header>
    );
};
